import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Shield, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TermsAndConditionsDialogProps {
  open: boolean;
  onAccept: () => void;
}

export function TermsAndConditionsDialog({ open, onAccept }: TermsAndConditionsDialogProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}} modal>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center flex-shrink-0">
          <div className="mx-auto mb-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary-foreground" />
          </div>
          <DialogTitle className="text-xl font-semibold">
            {t('terms.title')}
          </DialogTitle>
          <DialogDescription>
            {t('terms.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[40vh] sm:max-h-[400px] pr-4 my-4">
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('terms.section1.title')}
              </h4>
              <p className="text-muted-foreground">
                {t('terms.section1.content')}
              </p>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">{t('terms.section2.title')}</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• {t('terms.section2.item1')}</li>
                <li>• {t('terms.section2.item2')}</li>
                <li>• {t('terms.section2.item3')}</li>
                <li>• {t('terms.section2.item4')}</li>
              </ul>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">{t('terms.section3.title')}</h4>
              <p className="text-muted-foreground">
                {t('terms.section3.content')}
              </p>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">{t('terms.section4.title')}</h4>
              <p className="text-muted-foreground">
                {t('terms.section4.content')}
              </p>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">{t('terms.section5.title')}</h4>
              <p className="text-muted-foreground">
                {t('terms.section5.content')}
              </p>
            </div>

            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">
                {t('terms.lastUpdated')}: September 2024
              </p>
            </div>
          </div>
        </ScrollArea>

        <div className="space-y-4 flex-shrink-0 pt-2 border-t">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="terms" 
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked as boolean)}
            />
            <label 
              htmlFor="terms" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('terms.acceptanceCheckbox')}
            </label>
          </div>

          <DialogFooter>
            <Button 
              onClick={handleAccept} 
              disabled={!accepted}
              className="w-full"
            >
              {t('terms.acceptButton')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
} 